import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { humanize, money } from '@/lib/utils';
import { DataTable, Dialog, PageHeader, SearchFilterBar, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Checkbox } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { LabInstrumentsTab } from './LabInstrumentsTab';
import { LabManagerSettings } from './LabManagerSettings';
import { getPages } from './pagedList';

interface ReferenceRangeRow {
  sex: 'any' | 'male' | 'female';
  minAgeDays: number;
  maxAgeDays: number;
  low: number | null;
  high: number | null;
  text?: string;
}

export interface LabTestRow {
  id: string;
  code: string;
  name: string;
  departmentGroup: string;
  specimenType: string;
  containerType: string | null;
  tatMinutes: number;
  unit: string | null;
  resultType: string;
  selectOptions: string[];
  abnormalOptions?: string[];
  referenceRanges: ReferenceRangeRow[];
  criticalLow: number | null;
  criticalHigh: number | null;
  reportableLow?: number | null;
  reportableHigh?: number | null;
  method: string | null;
  basePriceMinor: number;
  isOutsourced: boolean;
  decimals: number | null;
  quickValues: string[];
  defaultValue: string | null;
  loincCode?: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface LabPanelRow {
  id: string;
  code: string;
  name: string;
  basePriceMinor: number;
  isActive: boolean;
  tests: { test: { id: string; code: string; name: string } }[];
}

interface LinkChargesReport {
  labTests: number;
  labPanels: number;
  radiologyStudies: number;
  chargeItemsCreated: number;
  departmentsFixed: number;
  tariffRowsAdded: number;
  invoiceLinesRelinked: number;
}

const LINK_REPORT_LABELS: { key: keyof LinkChargesReport; label: string }[] = [
  { key: 'labTests', label: 'Lab tests linked' },
  { key: 'labPanels', label: 'Lab panels linked' },
  { key: 'radiologyStudies', label: 'Radiology studies linked' },
  { key: 'chargeItemsCreated', label: 'Charge items created' },
  { key: 'departmentsFixed', label: 'Departments corrected' },
  { key: 'tariffRowsAdded', label: 'Tariff rows added' },
  { key: 'invoiceLinesRelinked', label: 'Old invoice lines re-linked' },
];

/**
 * The test master (SRS 8.1) — what the lab offers, how each result is typed
 * and what counts as normal for whom.
 *
 * A reference range is banded by sex and age because a haemoglobin that is
 * normal for a man is anaemia in a child; the bench never picks a band, the
 * server does it from the patient on the order.
 */
export function LabCataloguePage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'tests';
  const setTab = (next: string) => setParams({ tab: next }, { replace: true });
  const [search, setSearch] = useState('');
  const [linking, setLinking] = useState(false);
  const manage = can('lab.master.manage');

  const tests = useQuery({
    queryKey: ['lab-catalogue-tests'],
    queryFn: () => getPages<LabTestRow>('/lab/tests', { includeInactive: true, limit: 500 }, 4),
  });
  const panels = useQuery({
    queryKey: ['lab-panels'],
    queryFn: () => getPages<LabPanelRow>('/lab/panels', { includeInactive: true, limit: 200 }),
  });

  const needle = search.trim().toLowerCase();
  const matches = (code: string, name: string) => !needle || code.toLowerCase().includes(needle) || name.toLowerCase().includes(needle);
  const testRows = (tests.data ?? []).filter((t) => matches(t.code, t.name));
  const panelRows = (panels.data ?? []).filter((p) => matches(p.code, p.name));

  return (
    <>
      <PageHeader
        title={tr('lab', 'test_catalogue')}
        description={tr('lab', 'tests_panels_reference_ranges_prices_and_the')}
        actions={
          manage ? (
            <>
              <Button variant="outline" onClick={() => setLinking(true)}>
                {tr('lab', 'link_charge_items')}
              </Button>
              {tab === 'tests' && <Button onClick={() => navigate('/lab/catalogue/tests/new')}>{tr('lab', 'add_test')}</Button>}
              {tab === 'panels' && (
                <Button onClick={() => navigate('/lab/catalogue/panels/new')} disabled={!tests.data?.length}>
                  {tr('lab', 'add_panel')}
                </Button>
              )}
              {tab === 'instruments' && (
                <Button onClick={() => navigate('/lab/instruments/new')}>
                  <Plus className="me-1 h-4 w-4" /> {tr('lab', 'add_instrument')}
                </Button>
              )}
            </>
          ) : undefined
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'tests', label: tr('lab', 'tests'), badge: tests.data?.length },
          { id: 'panels', label: tr('lab', 'panels'), badge: panels.data?.length },
          { id: 'instruments', label: tr('lab', 'instruments') },
          ...(can('lab.settings.manage') ? [{ id: 'settings', label: tr('lab', 'lab_settings') }] : []),
        ]}
      />

      {tab === 'instruments' && <LabInstrumentsTab manage={manage} tests={tests.data ?? []} />}
      {tab === 'settings' && can('lab.settings.manage') && <LabManagerSettings />}

      {(tab === 'tests' || tab === 'panels') && (
        <SearchFilterBar
          search={{ value: search, onChange: setSearch, placeholder: tr('lab', 'search_by_name_or_code') }}
          onReset={search ? () => setSearch('') : undefined}
        />
      )}

      {tab === 'tests' && (
        <DataTable
          rows={testRows}
          loading={tests.isLoading} refreshing={tests.isFetching}
          error={tests.error}
          rowKey={(row) => row.id}
          onRowClick={manage ? (row) => navigate(`/lab/catalogue/tests/${row.id}/edit`) : undefined}
          empty={
            needle ? tr('lab', 'no_test_matches') : tr('lab', 'no_tests_yet_add_the_tests_this_lab_performs')
          }
          columns={[
            { header: tr('lab', 'code'), width: '110px', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
            {
              header: tr('lab', 'test'),
              sort: (row) => row.name,
              cell: (row) => (
                <div>
                  <p className={row.isActive ? '' : 'text-muted-foreground line-through'}>{row.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {row.specimenType}
                    {row.containerType ? ` · ${row.containerType}` : ''}
                  </p>
                </div>
              ),
            },
            { header: tr('lab', 'department'), sort: (row) => row.departmentGroup, cell: (row) => <Badge variant="secondary">{humanize(row.departmentGroup)}</Badge> },
            {
              header: tr('lab', 'result'),
              sort: (row) => row.resultType,
              cell: (row) => (
                <span className="text-sm">
                  {humanize(row.resultType)}
                  {row.unit ? <span className="text-muted-foreground"> · {row.unit}</span> : null}
                </span>
              ),
            },
            {
              header: tr('lab', 'reference'),
              cell: (row) => (
                <span className="text-xs text-muted-foreground">
                  {row.referenceRanges.length === 0
                    ? '—'
                    : row.referenceRanges.length === 1
                      ? describeRange(row.referenceRanges[0], row.unit)
                      : tr('lab', 'x_bands', { length: row.referenceRanges.length })}
                </span>
              ),
            },
            { header: tr('lab', 'tat'), align: 'right', sort: (row) => row.tatMinutes, cell: (row) => formatTat(row.tatMinutes) },
            { header: tr('lab', 'price'), align: 'right', sort: (row) => row.basePriceMinor, cell: (row) => money(row.basePriceMinor) },
            {
              header: tr('lab', 'status'),
              sort: (row) => row.isActive,
              cell: (row) => (
                <div className="flex gap-1">
                  <Badge variant={row.isActive ? 'success' : 'muted'}>{row.isActive ? tr('lab', 'active') : tr('lab', 'retired')}</Badge>
                  {row.isOutsourced && <Badge variant="warning">{tr('lab', 'send_out')}</Badge>}
                </div>
              ),
            },
          ]}
        />
      )}

      {tab === 'panels' && (
        <DataTable
          rows={panelRows}
          loading={panels.isLoading} refreshing={panels.isFetching}
          error={panels.error}
          rowKey={(row) => row.id}
          onRowClick={manage ? (row) => navigate(`/lab/catalogue/panels/${row.id}/edit`) : undefined}
          empty={needle ? tr('lab', 'no_panel_matches') : tr('lab', 'no_panels_yet_a_panel_is_what_a_doctor_orders')}
          columns={[
            { header: tr('lab', 'code'), width: '110px', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
            { header: tr('lab', 'panel_2'), sort: (row) => row.name, cell: (row) => row.name },
            {
              header: tr('lab', 'tests'),
              cell: (row) => (
                <span className="text-xs text-muted-foreground">{row.tests.map((t) => t.test.name).join(', ')}</span>
              ),
            },
            { header: tr('lab', 'price'), align: 'right', sort: (row) => row.basePriceMinor, cell: (row) => money(row.basePriceMinor) },
            {
              header: tr('lab', 'status'),
              sort: (row) => row.isActive,
              cell: (row) => <Badge variant={row.isActive ? 'success' : 'muted'}>{row.isActive ? tr('lab', 'active') : tr('lab', 'retired')}</Badge>,
            },
          ]}
        />
      )}

      {linking && <LinkChargesDialog onClose={() => setLinking(false)} />}
    </>
  );
}

function LinkChargesDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [relinkInvoiceLines, setRelinkInvoiceLines] = useState(false);
  const [report, setReport] = useState<LinkChargesReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const link = useMutation({
    mutationFn: () => post<LinkChargesReport>('/lab/catalogue/link-charges', { relinkInvoiceLines }),
    onSuccess: (result) => {
      setReport(result);
      toast.success(
        tr('lab', 'charge_items_linked'),
        tr('lab', 'x_created_x_tariff_rows_added_x_invoice_lines', { chargeItemsCreated: result.chargeItemsCreated, tariffRowsAdded: result.tariffRowsAdded, invoiceLinesRelinked: result.invoiceLinesRelinked }),
      );
      void queryClient.invalidateQueries({ queryKey: ['lab-catalogue-tests'] });
      void queryClient.invalidateQueries({ queryKey: ['lab-panels'] });
      void queryClient.invalidateQueries({ queryKey: ['lab-tests-search'] });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('lab', 'link_charge_items')}
      description={tr('lab', 'give_every_test_panel_and_imaging_study_a')}
      footer={
        report ? (
          <Button onClick={onClose}>{tr('lab', 'done')}</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {tr('lab', 'cancel')}
            </Button>
            <Button
              onClick={() => {
                setError(null);
                link.mutate();
              }}
              loading={link.isPending}
            >
              {tr('lab', 'link_charge_items')}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4">
        {report ? (
          <Alert variant="success" title={tr('lab', 'finished')}>
            <ul className="mt-1 grid gap-x-6 gap-y-0.5 sm:grid-cols-2">
              {LINK_REPORT_LABELS.map((entry) => (
                <li key={entry.key} className="flex justify-between gap-3">
                  <span>{entry.label}</span>
                  <span className="tabular font-semibold">{report[entry.key]}</span>
                </li>
              ))}
            </ul>
          </Alert>
        ) : (
          <>
            <p className="text-sm">
              {tr('lab', 'safe_to_run_again_anything_already_linked_is')}
            </p>
            <Checkbox
              checked={relinkInvoiceLines}
              onCheckedChange={setRelinkInvoiceLines}
              label={tr('lab', 'also_fix_old_invoice_lines')}
              description={
                <>
                  {tr('lab', 'sets_the_charge_item_on_lab_and_imaging_lines')}
                </>
              }
            />
          </>
        )}
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

function formatTat(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)} h`;
  return `${Math.round(minutes / (60 * 24))} d`;
}

function describeRange(range: ReferenceRangeRow, unit: string | null): string {
  if (range.text) return range.text;
  const suffix = unit ? ` ${unit}` : '';
  if (range.low !== null && range.high !== null) return `${range.low} – ${range.high}${suffix}`;
  if (range.high !== null) return `< ${range.high}${suffix}`;
  if (range.low !== null) return `> ${range.low}${suffix}`;
  return '—';
}
